import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import { useFormState } from './formContext';
import { PickerFeedback, PickerOptionContent, mergePickerRefs, pickerKeyDown, usePickerAnnouncement, usePickerValidity } from './pickerSupport';
import type { MultiSelectProps, PickerOption } from './pickerTypes';

export type { MultiSelectProps } from './pickerTypes';

export const MultiSelect = forwardRef<HTMLInputElement, MultiSelectProps>(function MultiSelect({
  id, name, required, label, value, query, options, loading, error, onQueryChange, onChange, onRetry,
  disabled = false, disabledReason, unavailable, placeholder, renderOption, className,
  'aria-required': ariaRequired, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy,
}, ref) {
  const input = useRef<HTMLInputElement>(null);
  const composing = useRef(false);
  const form = useFormState();
  const inactive = disabled || Boolean(unavailable) || Boolean(form?.busy);
  const reason = unavailable || disabledReason || (form?.busy ? 'Unavailable while saving.' : undefined);
  const items = useMemo(() => loading || error || inactive ? []
    : options.filter((option) => !value.some((selected) => selected.id === option.id)), [options, value, loading, error, inactive]);
  const selectedItems = useMemo(() => [...value], [value]);
  useImperativeHandle(ref, () => input.current!);
  usePickerValidity(input, required, value.length > 0);
  const { getSelectedItemProps, getDropdownProps, addSelectedItem, removeSelectedItem } = useMultipleSelection<PickerOption>({
    selectedItems, itemToKey: (item) => item?.id ?? '',
    onSelectedItemsChange: ({ selectedItems: next }) => {
      if (!inactive) { onChange(next ?? []); form?.markChanged(); }
    },
  });
  const { isOpen, highlightedIndex, getLabelProps, getInputProps, getMenuProps, getItemProps, getToggleButtonProps } = useCombobox<PickerOption>({
    id: `${id}-picker`, inputId: id, labelId: `${id}-picker-label`, menuId: `${id}-menu`,
    getItemId: (index) => `${id}-option-${index}`,
    items, itemToString: (item) => item?.label ?? '', itemToKey: (item) => item?.id ?? '',
    isItemDisabled: (item) => Boolean(item.disabledReason), inputValue: query, selectedItem: null,
    onInputValueChange: ({ inputValue }) => onQueryChange(inputValue ?? ''),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem && !inactive && !selectedItem.disabledReason
        && !value.some((option) => option.id === selectedItem.id)) addSelectedItem(selectedItem);
    },
    stateReducer: (state, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return { ...changes, isOpen: true, inputValue: '' };
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return { ...changes, selectedItem: null, inputValue: state.inputValue };
        default: return changes;
      }
    },
  });
  const open = isOpen && !inactive;
  const announcement = usePickerAnnouncement(query, items.length, loading, error, unavailable, open);
  const dropdownProps = getDropdownProps({ preventKeyAction: isOpen });
  const inputProps = getInputProps({
    ...dropdownProps, ref: mergePickerRefs(input, dropdownProps.ref),
    name, required: required && value.length === 0, disabled: inactive, placeholder,
    'aria-required': ariaRequired || required || undefined, 'aria-invalid': ariaInvalid,
    'aria-describedby': [describedBy, `${id}-count`, reason ? `${id}-availability` : ''].filter(Boolean).join(' '),
    onBlur: () => onQueryChange(''),
  });
  return <div className={`ui-picker ${className ?? ''}`}>
    {/* Names the listbox only. The input is labelled by FormField's <label>; a second
        <label for> would make its accessible name "Label Label". */}
    <span id={getLabelProps().id} className="ui-picker__label">{label}</span>
    {value.length > 0 && <ul className="ui-picker__chips" aria-label={`Selected ${label}`}>
      {value.map((option, index) => {
        const selectedProps = getSelectedItemProps({ selectedItem: option, index });
        return <li key={option.id} {...selectedProps} className="ui-picker__chip"
          tabIndex={inactive ? -1 : selectedProps.tabIndex}
          onKeyDown={(event) => { if (!inactive) selectedProps.onKeyDown?.(event); }}
          onClick={(event) => { if (!inactive) selectedProps.onClick?.(event); }}>
          <span>{option.label}</span><button type="button" className="ui-button" disabled={inactive}
            aria-label={`Remove ${option.label}`} onClick={(event) => { event.stopPropagation(); removeSelectedItem(option); }}>×</button>
        </li>;
      })}
    </ul>}
    <div className="ui-picker__control">
      <input {...inputProps} className="ui-control" onKeyDown={(event) =>
        pickerKeyDown(event, open, highlightedIndex, composing.current, inputProps.onKeyDown)}
      aria-expanded={open}
      aria-activedescendant={open && items[highlightedIndex] ? `${id}-option-${highlightedIndex}` : undefined}
      onCompositionStart={() => { composing.current = true; }} onCompositionEnd={() => { composing.current = false; }} />
      <button {...getToggleButtonProps({ disabled: inactive })} type="button" className="ui-button"
        aria-label={`Toggle ${label} options`}>▾</button>
    </div>
    <p id={`${id}-count`} className="ui-field__help">{value.length} selected</p>
    <ul {...getMenuProps()} className="ui-picker__menu" hidden={!open || !items.length} aria-busy={loading || undefined}>
      {open && items.map((option, index) => <li key={option.id} {...getItemProps({ item: option, index })}
        aria-selected={false} aria-disabled={Boolean(option.disabledReason)}
        aria-describedby={option.disabledReason ? `${id}-reason-${index}` : undefined}
        className={`ui-picker__option ${highlightedIndex === index ? 'ui-picker__option--active' : ''}`}>
        {renderOption ? renderOption(option) : <PickerOptionContent option={option} />}
        {option.disabledReason && <span id={`${id}-reason-${index}`} className="ui-picker__detail">{option.disabledReason}</span>}
      </li>)}
    </ul>
    <PickerFeedback id={id} loading={loading} error={error} onRetry={onRetry} unavailable={unavailable}
      disabledReason={reason} disabled={inactive} empty={open && !items.length} />
    <span className="ui-picker__label" role="status" aria-live="polite" aria-atomic="true">{announcement}</span>
  </div>;
});
